import React, { useState } from "react";
import {
	Typography,
	Grid2 as Grid,
	Collapse,
	Box,
	IconButton,
	Divider,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useStyles } from "./OutstandingEdiInvoiceLineItemDetails.styles";
import { EdiInvoiceLineItem } from "../../common/EdiInvoice";
import { AdditionalProductDetails } from "../../util/ProductUtilities";
import { PurchaseOrderLineItem } from "../../common/PurchaseOrder";
import QuantitySelector from "../Common/QuantitySelector/QuantitySelector";

type EdiOutstandingInvoiceLineItemDetailsProps = {
	invoiceLineItem: EdiInvoiceLineItem;
	purchaseOrderLineItem?: PurchaseOrderLineItem;
	productDetail?: AdditionalProductDetails;
	currentCount?: number;
	onCountChange?: (sku: string, count: number) => void;
};

const EdiOutstandingInvoiceLineItemDetails: React.FC<EdiOutstandingInvoiceLineItemDetailsProps> = ({
	invoiceLineItem,
	purchaseOrderLineItem,
	productDetail,
	currentCount = 0,
	onCountChange,
}) => {
	const classes = useStyles();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const [isExpanded, setIsExpanded] = useState(false);
	const countValue = currentCount;

	const toggleExpand = () => {
		setIsExpanded(!isExpanded);
	};

	const handleQuantityChange = (newValue: number) => {
		if (onCountChange) {
			onCountChange(invoiceLineItem.sku, newValue);
		}
	};

	// Determine if counter meets requirement (counter >= SOH + Invoiced Quantity AND count > 0)
	const requiredCount = (productDetail?.physicalStockOnHand || 0) + invoiceLineItem.quantity;
	const meetsRequirement = countValue >= requiredCount && countValue > 0;
	const rowBackgroundColor = meetsRequirement ? theme.palette.custom.indicator.full : theme.palette.background.paper;

	return (
		<React.Fragment>
			{/* Main row with responsive columns */}
			<Grid
				container
				className={classes.lineItemRow}
				alignItems="center"
				style={{
					backgroundColor: rowBackgroundColor,
					padding: isMobile ? "8px 8px" : "8px 16px",
				}}
			>
				<Grid size={4} className={classes.centerAligned}>
					<Box display="flex" alignItems="center">
						<IconButton
							size="small"
							onClick={(e) => {
								e.stopPropagation();
								toggleExpand();
							}}
							style={{ padding: isMobile ? "4px" : "8px" }}
						>
							{isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
						</IconButton>
						<Typography variant={isMobile ? "caption" : "body2"} style={{ fontWeight: 500 }}>
							{invoiceLineItem.sku}
						</Typography>
					</Box>
				</Grid>
				<Grid size={2} className={isMobile ? classes.rightAlignedMobile : classes.rightAligned}>
					<Typography variant={isMobile ? "caption" : "body2"}>{productDetail?.physicalStockOnHand}</Typography>
				</Grid>
				<Grid size={2} className={isMobile ? classes.rightAlignedMobile : classes.rightAligned}>
					<Typography variant={isMobile ? "caption" : "body2"}>{invoiceLineItem.quantity}</Typography>
				</Grid>
				<Grid size={4}>
					<Box display="flex" justifyContent="flex-end" width="100%">
						<QuantitySelector productQuantity={countValue} onQuantityChange={handleQuantityChange} />
					</Box>
				</Grid>
			</Grid>

			{/* Expandable panel with additional details */}
			<Collapse in={isExpanded} timeout="auto" unmountOnExit>
				<Box className={classes.expandablePanel}>
					<Grid container spacing={1} className={classes.expandablePanelContent}>
						<Grid size={{ xs: 12, sm: 4 }}>
							<Typography className={classes.panelLabel}>Product Description</Typography>
							<Typography variant="body2" className={classes.panelValue}>
								{purchaseOrderLineItem?.description || "N/A"}
							</Typography>
						</Grid>
						<Grid size={{ xs: 6, sm: 4 }}>
							<Typography className={classes.panelLabel}>Barcode</Typography>
							<Typography variant="body2" className={classes.panelValue}>
								{purchaseOrderLineItem?.barcode || "N/A"}
							</Typography>
						</Grid>
						<Grid size={{ xs: 6, sm: 4 }}>
							<Typography className={classes.panelLabel}>Product Model</Typography>
							<Typography variant="body2" className={classes.panelValue}>
								{purchaseOrderLineItem?.model || "N/A"}
							</Typography>
						</Grid>

						<Grid size={12}>
							<Divider />
						</Grid>

						{/* SOH Breakdown Section */}
						<Grid size={{ xs: 4, sm: 2 }}>
							<Typography variant="caption" color="textSecondary">
								Available
							</Typography>
							<Typography variant="body2" className={classes.panelValue}>
								{productDetail?.availableStockOnHand ?? "N/A"}
							</Typography>
						</Grid>
						<Grid size={{ xs: 4, sm: 2 }}>
							<Typography variant="caption" color="textSecondary">
								On Order
							</Typography>
							<Typography variant="body2" className={classes.panelValue}>
								{productDetail?.stockOnOrder ?? "N/A"}
							</Typography>
						</Grid>
						<Grid size={{ xs: 4, sm: 2 }}>
							<Typography variant="caption" color="textSecondary">
								CO
							</Typography>
							<Typography variant="body2" className={classes.panelValue}>
								{productDetail?.stockOnCustomerOrder ?? "N/A"}
							</Typography>
						</Grid>
						<Grid size={{ xs: 4, sm: 2 }}>
							<Typography variant="caption" color="textSecondary">
								Total SOH
							</Typography>
							<Typography variant="body2" className={classes.panelValue}>
								{productDetail?.physicalStockOnHand ?? "N/A"}
							</Typography>
						</Grid>
						<Grid size={{ xs: 4, sm: 2 }}>
							<Typography variant="caption" color="textSecondary">
								Invoiced Qty
							</Typography>
							<Typography variant="body2" className={classes.panelValue}>
								{invoiceLineItem.quantity}
							</Typography>
						</Grid>
					</Grid>
				</Box>
			</Collapse>
		</React.Fragment>
	);
};

export default EdiOutstandingInvoiceLineItemDetails;
